"use client";
import React, { useEffect, useId, useRef, useState } from "react";
import { Input } from "@ecco/ui/input";
import { googleMapsUtils } from "@lib/maps/googleMapsUtils";
import { Box } from "@ecco/ui/box";
import { List, ListItem } from "@ecco/ui/list";
import { Text } from "@ecco/ui/text";
import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";
import { InputGroup } from "@ecco/ui/input-group";
import { InputLeftElement } from "@ecco/ui/input-element";
import { ECCOIcon } from "@ecco/ui/ecco-icon";
import { getSuggestions } from "./getSuggestions";
import { getPlaceDetails } from "./getPlaceDetails";
import { RESTRICT_SEARCH_TO_CURRENT_MARKET } from "@components/store-locator/constants/restrictSearchToMarket";
import { SEARCH, SEARCH_SUGGESTION } from "@e2e/helpers/store-locator/constants/common";
import { useLocale } from "next-intl";
import type {
    SearchResultLocation,
    StoreLocatorLocation,
} from "@components/store-locator/controller";
import { getGeocodeResults } from "./getGeocodeResult";
import { type SystemStyleObject } from "@ecco/ui/theme-tools";

const MIN_QUERY_LENGTH = 3;

type AutocompleteInputProps = {
    value?: string;
    /** Callback fired when the input value changes */
    onChange: (value: string) => void;
    /** Callback fired when a place is selected from suggestions */
    onSelect: (place: SearchResultLocation) => void;
    placeholder?: string;
    disabled?: boolean;
    "aria-label"?: string;
    location?: StoreLocatorLocation;
    sx?: SystemStyleObject;
};

export const AutocompleteInput = ({
    placeholder,
    value = "",
    onChange,
    onSelect,
    disabled = false,
    "aria-label": ariaLabel,
    location,
    sx,
}: AutocompleteInputProps) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | undefined>(
        undefined
    );
    const [predictions, setPredictions] = useState<google.maps.places.AutocompleteSuggestion[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [isLoading, setIsLoading] = useState(false);

    const locale = useLocale();

    // Generate unique IDs for aria attributes
    const inputId = useId();
    const listboxId = useId();
    const comboboxId = useId();

    const resetState = () => {
        setPredictions([]);
        setIsOpen(false);
        setActiveIndex(-1);
        setIsLoading(false);
    };

    // Initialize Google Maps Services
    useEffect(() => {
        const initializeServices = async () => {
            if (disabled) return;

            try {
                await googleMapsUtils.loadMaps(locale);

                // Create session token for the user session
                if (!sessionTokenRef.current) {
                    sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
                }
            } catch (error) {
                Logger.error(ServiceType.GOOGLE, "Failed to initialize Google Maps services", {
                    tag: LogTag.STORES,
                    error,
                });
            }
        };

        initializeServices();

        return () => {
            sessionTokenRef.current = undefined;
        };
    }, [disabled, locale]);

    const performPredictionSearch = async (query: string) => {
        onChange(query);

        // Reset selection state when typing
        setActiveIndex(-1);

        // If no query is available
        if (!query?.trim()) {
            resetState();
            return;
        }

        if (query?.trim().length < MIN_QUERY_LENGTH) {
            resetState();
            return;
        }
        setIsOpen(true);

        setIsLoading(true);
        const suggestions = await getSuggestions(
            query,
            sessionTokenRef.current,
            RESTRICT_SEARCH_TO_CURRENT_MARKET,
            location
        );
        setIsLoading(false);
        if (suggestions && suggestions.length > 0) {
            setPredictions(suggestions);
        } else {
            setPredictions([]);
            setIsOpen(false);
        }
    };

    const handleOnInput = (event: React.FormEvent<HTMLInputElement>) => {
        const query = event.currentTarget.value;
        performPredictionSearch(query);
    };

    const performGeocodingSearch = async () => {
        if (value.trim().length < MIN_QUERY_LENGTH) {
            return;
        }
        const result = await getGeocodeResults(value, RESTRICT_SEARCH_TO_CURRENT_MARKET, location);
        if (!Array.isArray(result) || result.length === 0) {
            return;
        }
        const firstResult = result[0];

        resetState();

        onSelect({
            type: "geocode",
            coordinates: firstResult.geometry.location,
            object: firstResult,
        });
    };

    // Handle keyboard navigation events
    const handleKeyDown = async (event: React.KeyboardEvent<HTMLInputElement>) => {
        switch (event.key) {
            case "ArrowDown":
                if (!isOpen) break;
                event.preventDefault();
                setActiveIndex((prev) => (prev < predictions.length - 1 ? prev + 1 : 0));
                break;
            case "ArrowUp":
                if (!isOpen) break;
                event.preventDefault();
                setActiveIndex((prev) => (prev > 0 ? prev - 1 : predictions.length - 1));
                break;
            case "Enter":
                event.preventDefault();
                if (activeIndex >= 0 && predictions[activeIndex]) {
                    handlePredictionSelect(predictions[activeIndex]);
                    break;
                }
                await performGeocodingSearch();
                setIsOpen(false);
                setActiveIndex(-1);
                break;
            case "Escape":
                setIsOpen(false);
                setActiveIndex(-1);
                break;
            default:
                setIsOpen(true);
                break;
        }
    };

    const handleBlur = () => {
        // Delay closing to allow for click events on suggestions
        setTimeout(() => {
            setIsOpen(false);
            setActiveIndex(-1);
        }, 150);
    };

    const handleFocus = () => {
        // Only trigger search if there is already a value in the input
        if (value.trim()) {
            setIsOpen(true);
            performPredictionSearch(value);
        }
    };

    // Callback for selecting a prediction from the list
    const handlePredictionSelect = async (
        prediction: google.maps.places.AutocompleteSuggestion
    ) => {
        if (!sessionTokenRef.current) {
            Logger.warn(
                ServiceType.GOOGLE,
                "No session token available for place details request",
                {
                    tag: LogTag.STORES,
                }
            );
        }
        if (!prediction?.placePrediction?.mainText?.text) {
            return;
        }

        // Update input value immediately for the UI
        onChange(prediction.placePrediction?.mainText?.text);
        resetState();

        try {
            const { place, isFromCache } = await getPlaceDetails(prediction, location);

            // If we call the places details API the token is invalidaded and every subsequent autocomplete call will count as a new session.
            // So when we get result from Google we need to generate new token.
            // https://developers.google.com/maps/documentation/places/web-service/using-session-tokens#end_the_session
            if (!isFromCache) {
                sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
            }
            if (place) {
                onSelect({
                    type: "place",
                    coordinates: place.location,
                    object: place,
                });
            }
        } catch (error) {
            Logger.error(ServiceType.GOOGLE, "Failed to fetch place details", {
                tag: LogTag.STORES,
                error,
            });
        }
    };

    useEffect(() => {
        // if navigating by keyboard make sure we scroll the active item into view
        const listElement = document.getElementById(`${listboxId}-${activeIndex}`);
        if (listElement) {
            listElement.scrollIntoView({ block: "nearest" });
        }
    }, [activeIndex]);

    return (
        <Box position="relative" role="combobox" aria-expanded={isOpen} id={comboboxId} sx={sx}>
            <InputGroup>
                <InputLeftElement position={["absolute"]} zIndex={3}>
                    <ECCOIcon name="search" color="black" size="sm" />
                </InputLeftElement>
                <Input
                    ref={inputRef}
                    id={inputId}
                    value={value}
                    onInput={handleOnInput}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    placeholder={placeholder}
                    disabled={disabled}
                    autoComplete="off"
                    data-testid={SEARCH}
                    role="textbox"
                    aria-label={ariaLabel || "Search for a location"}
                    aria-autocomplete="list"
                    aria-controls={isOpen ? listboxId : undefined}
                    aria-activedescendant={
                        activeIndex >= 0 ? `${listboxId}-${activeIndex}` : undefined
                    }
                    aria-busy={isLoading}
                    _focus={{
                        boxShadow: "none",
                        outline: "2px solid black",
                        outlineOffset: "-2px",
                    }}
                />
            </InputGroup>

            {/* Loading state indicator */}
            {isLoading && (
                <Box
                    position="absolute"
                    top="100%"
                    left="0"
                    right="0"
                    bg="white"
                    border="1px"
                    borderColor="gray.200"
                    zIndex="dropdown"
                    borderRadius="md"
                    shadow="lg"
                    px={3}
                    py={2}
                    mt={1}
                >
                    <Text fontSize="sm" color="gray.500">
                        ...
                    </Text>
                </Box>
            )}

            {/* Autocomplete suggestions list */}
            {isOpen && !isLoading && predictions.length > 0 && (
                <Box
                    position="absolute"
                    top="100%"
                    left="0"
                    right="0"
                    bg="white"
                    border="1px"
                    borderColor="gray.200"
                    borderRadius="md"
                    shadow="lg"
                    maxHeight="300px"
                    zIndex="dropdown"
                    overflowY="auto"
                    mt={1}
                >
                    <List
                        id={listboxId}
                        role="listbox"
                        aria-label="Location suggestions"
                        m={0}
                        p={0}
                    >
                        {predictions.map((prediction, index) => (
                            <ListItem
                                key={
                                    prediction?.placePrediction?.placeId ??
                                    prediction?.placePrediction?.mainText?.text
                                }
                                id={`${listboxId}-${index}`}
                                role="option"
                                aria-selected={index === activeIndex}
                                cursor="pointer"
                                px={3}
                                py={2}
                                bg={index === activeIndex ? "gray.100" : "transparent"}
                                _hover={{ bg: "gray.100" }}
                                onClick={() => handlePredictionSelect(prediction)}
                                onMouseEnter={() => setActiveIndex(index)}
                                data-testid={SEARCH_SUGGESTION}
                            >
                                <Text fontWeight="medium" fontSize="sm">
                                    {prediction.placePrediction?.mainText?.text}
                                </Text>
                                {prediction.placePrediction?.secondaryText?.text && (
                                    <Text fontSize="xs" color="gray.600">
                                        {prediction.placePrediction?.secondaryText?.text}
                                    </Text>
                                )}
                            </ListItem>
                        ))}
                    </List>
                </Box>
            )}
        </Box>
    );
};
